'use client';

import type { FundingCheckout } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent, useRef } from 'react';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIconSolid } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { dismissToast, showToast } from '@/lib/ui/toast';

export function FundingConnectionRequired({
  reason,
  onRefresh,
  canAuthenticate = false,
  busy = false,
  error = false,
  bordered = false,
}: {
  reason: FundingCheckout['authenticationReason'];
  location?: FundingCheckout['location'];
  onRefresh?: () => void;
  canAuthenticate?: boolean;
  busy?: boolean;
  error?: boolean;
  bordered?: boolean;
}): ReactNode {
  const t = useTranslations('onramp');
  const refreshRequested = useRef(false);
  const announceRefresh = useEffectEvent((): void => {
    showToast({
      id: TOAST_IDS.ONRAMP_ELIGIBILITY_REFRESH,
      message: t('refreshing_eligibility'),
      variant: 'loading',
      duration: Number.POSITIVE_INFINITY,
    });
  });
  useEffect(() => {
    if (!busy || !refreshRequested.current) return;
    announceRefresh();
    return (): void => {
      refreshRequested.current = false;
      dismissToast(TOAST_IDS.ONRAMP_ELIGIBILITY_REFRESH);
    };
  }, [busy]);
  function refreshEligibility(): void {
    if (!onRefresh || busy || refreshRequested.current) return;
    refreshRequested.current = true;
    onRefresh();
  }
  const mismatch = reason === 'account_mismatch';
  const message = !canAuthenticate
    ? t('no_eligible_methods')
    : reason === 'guest_unavailable' || reason === 'region' || reason === 'location_unavailable'
      ? t('connect_reason_guest_unavailable')
      : mismatch
        ? t('connect_reason_account_mismatch')
        : t('connect_reason_provider');
  if (canAuthenticate && !error && (!reason || reason === 'provider')) return null;
  const title = !canAuthenticate
    ? t('no_eligible_title')
    : mismatch
      ? t('payment_connection_required')
      : t('guest_checkout_unavailable');
  return (
    <aside
      className={`flex items-start gap-2 ${bordered ? 'rounded-control border border-border-primary p-3' : 'border-t border-border-secondary pt-3'}`}
      aria-label={title}
    >
      <Icon
        icon={InformationCircleIconSolid}
        className="mt-0.5 size-icon-sm shrink-0 text-foreground-secondary"
        aria-hidden="true"
      />
      <div className="flex flex-col gap-1">
        <h3 className="text-caption font-medium">{title}</h3>
        <p className="text-caption text-foreground-secondary">
          {error ? t('provider_unavailable') : message}{' '}
          {onRefresh ? (
            <button
              type="button"
              className="cursor-pointer underline underline-offset-2 hover:text-foreground disabled:cursor-wait"
              disabled={busy}
              onClick={refreshEligibility}
            >
              {t('refresh_eligibility')}
            </button>
          ) : null}
        </p>
      </div>
    </aside>
  );
}
